import React, { useState } from "react";
import { Form, Icon } from "semantic-ui-react";
import { useFormik } from "formik";
import { Auth } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { inititalValues, validationSchema } from "./LoginForm.form";
import "./LoginForm.scss";
import { useLanguage } from "../../../../contexts";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const authController = new Auth();

export function LoginForm() {
  const { login, ssoError, ssoChallenge } = useAuth();
  // Si se llegó desde la otra app y el traspaso falló, el motivo se muestra
  // acá; si allá no se validó 2FA y acá es obligatorio, arranca pidiendo el código.
  const [error, setError] = useState(ssoError || "");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [keepSession, setKeepSession] = useState(true);
  const [challengeToken, setChallengeToken] = useState(ssoChallenge || null);
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);

  const togglePasswordVisibility = (e) => {
    e.preventDefault();
    setPasswordVisible(!passwordVisible);
  };

  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  const formik = useFormik({
    initialValues: inititalValues(),
    validationSchema: validationSchema(t),
    validateOnChange: false,
    onSubmit: async (formValue) => {
      try {
        const response = await authController.login({ ...formValue, keepSession });
        if (response.requires2FA) {
          setChallengeToken(response.challengeToken);
          return;
        }
        authController.setAccessToken(response.access, keepSession);
        authController.setRefreshToken(response.refresh);
        toast.success(t("login_success"));
        setTimeout(() => login(response.access), 800);
      } catch (error) {
        toast.error(error.msg || t("server_connection_error"));
        setError(error.msg);
      }
    },
  });

  const recoverPassword = () => (window.location.href = "/recover");

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !formik.isSubmitting) {
      e.preventDefault();
      formik.handleSubmit();
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (!code || verifying) return;
    setVerifying(true);
    try {
      const response = await authController.verifyEmailCode(challengeToken, code, keepSession);
      authController.setAccessToken(response.access, keepSession);
      toast.success(t("login_success"));
      setTimeout(() => login(response.access), 800);
    } catch (error) {
      toast.error(error.msg || t("server_connection_error"));
    } finally {
      setVerifying(false);
    }
  };

  const handleResendCode = async () => {
    if (resending) return;
    setResending(true);
    try {
      const response = await authController.resendEmailCode(challengeToken);
      setChallengeToken(response.challengeToken);
      setCode("");
      toast.success(t("code_resent") || "Código reenviado");
    } catch (error) {
      toast.error(error.msg || t("server_connection_error"));
    } finally {
      setResending(false);
    }
  };

  if (challengeToken) {
    return (
      <div className="login-modal-form">
        <Form onSubmit={handleVerifyCode} className="login-form">
          <p>{t("two_fa_prompt") || "Ingresá el código de 6 dígitos que te enviamos por email."}</p>
          <Form.Field>
            <label>{t("verification_code") || "Código"}</label>
            <input
              name="code"
              type="text"
              inputMode="numeric"
              maxLength={6}
              autoComplete="one-time-code"
              placeholder="000000"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              autoFocus
            />
          </Form.Field>

          <Form.Button type="submit" primary fluid size="large" loading={verifying} disabled={code.length !== 6}>
            {t("verify") || "Verificar"}
          </Form.Button>

          <a className="recover-password" onClick={handleResendCode}>
            {resending ? (t("sending") || "Enviando…") : (t("resend_code") || "Reenviar código")}
          </a>

          <a className="recover-password" onClick={() => { setChallengeToken(null); setCode(""); }}>
            {t("back") || "Volver"}
          </a>
        </Form>

        <ToastContainer
          position="top-center"
          autoClose={1200}
          hideProgressBar
          newestOnTop={false}
          closeOnClick={false}
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      </div>
    );
  }

  return (
    <div className="login-modal-form">
      {/* <div className="login-modal-form__logo">
        <Image src="/logo.png" size="small" centered />
      </div> */}

      <Form onSubmit={formik.handleSubmit} className="login-form">
        <Form.Field error={!!formik.errors.email}>
          <label>{t("email")}</label>
          <div className="password-input-wrapper">
            <input
              name="email"
              type="email"
              autoComplete="username"
              placeholder={t("email")}
              onChange={formik.handleChange}
              value={formik.values.email}
              onKeyDown={handleKeyPress}
            />
          </div>
          {formik.errors.email && (
            <div className="ui pointing above prompt label">
              {formik.errors.email}
            </div>
          )}
        </Form.Field>

        <Form.Field error={!!formik.errors.password}>
          <label>{t("password")}</label>
          <div className="password-input-wrapper">
            <input
              name="password"
              type={passwordVisible ? "text" : "password"}
              autoComplete="current-password"
              placeholder={t("password")}
              onChange={formik.handleChange}
              value={formik.values.password}
              onKeyDown={handleKeyPress}
            />
            <button
              type="button"
              className="password-input-wrapper__eye"
              onClick={togglePasswordVisibility}
            >
              <Icon name={passwordVisible ? "eye slash" : "eye"} />
            </button>
          </div>
          {formik.errors.password && (
            <div className="ui pointing above prompt label">
              {formik.errors.password}
            </div>
          )}
        </Form.Field>

        <div className="login-form__row">
          <label className="login-form__checkbox">
            <input
              type="checkbox"
              checked={keepSession}
              onChange={(e) => setKeepSession(e.target.checked)}
            />
            {t("keep_session_active")}
          </label>
          <a className="recover-password" onClick={recoverPassword}>
            {t("i_forgot_my_password")}
          </a>
        </div>

        <Form.Button
          type="submit"
          primary
          fluid
          size="large"
          loading={formik.isSubmitting}
        >
          {t("sign_in")}
        </Form.Button>

        {error && <p className="login-error">{error}</p>}
      </Form>

      <ToastContainer
        position="top-center"
        autoClose={1200}
        hideProgressBar
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
}


